import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { Organization } from '../models/organization';
import { ParentOrganization } from '../models/parent-organization';
import {
  OrganizationLicenseEntitlements,
  UpdateOrganizationLicenseEntitlementsRequest,
} from '../models/license-entitlement';
import {
  OrganizationLicenseUsageMonthAvailability,
  OrganizationLicenseUsageReport,
} from '../models/license-usage-report';
import { WorkflowDataService } from '../data-table/workflow-data.service';
import { SFSOptions } from '../data-table';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService extends WorkflowDataService<Organization> {
  private organizationsNameMap: { [key: string]: string } = {};

  protected override defaultSFSOptions: Partial<SFSOptions> = {
    sortColumn: 'name',
    sortOrder: 'asc',
  };

  protected override get apiEndpoint(): string {
    return '/organization';
  }

  getApiKey(): Observable<string> {
    return this.http.get<string>(
      `${this.baseUrl + this.apiEndpoint}/self/api-key`,
    );
  }

  getDeviceScope(): Observable<{ organizations: ParentOrganization[] }> {
    return this.http.get<{ organizations: ParentOrganization[] }>(
      `${this.baseUrl + this.apiEndpoint}/self/device-scope`,
    );
  }

  getLicenseEntitlements(
    organizationId: string,
  ): Observable<OrganizationLicenseEntitlements> {
    return this.http.get<OrganizationLicenseEntitlements>(
      `${this.baseUrl + this.apiEndpoint}/${organizationId}/license-entitlements`,
    );
  }

  updateLicenseEntitlements(
    organizationId: string,
    request: UpdateOrganizationLicenseEntitlementsRequest,
  ): Observable<OrganizationLicenseEntitlements> {
    return this.http.put<OrganizationLicenseEntitlements>(
      `${this.baseUrl + this.apiEndpoint}/${organizationId}/license-entitlements`,
      request,
    );
  }

  getLicenseUsageReport(
    organizationId: string,
    month: string,
    types: readonly string[] = [],
  ): Observable<OrganizationLicenseUsageReport> {
    let params = new HttpParams().set('month', month);
    types.forEach((type) => {
      params = params.append('types', type);
    });

    return this.http.get<OrganizationLicenseUsageReport>(
      `${this.baseUrl + this.apiEndpoint}/${organizationId}/license-usage`,
      { params },
    );
  }

  getLicenseUsageMonthAvailability(
    organizationId: string,
    types: readonly string[] = [],
  ): Observable<OrganizationLicenseUsageMonthAvailability> {
    let params = new HttpParams();
    types.forEach((type) => {
      params = params.append('types', type);
    });

    return this.http.get<OrganizationLicenseUsageMonthAvailability>(
      `${this.baseUrl + this.apiEndpoint}/${organizationId}/license-usage/months`,
      { params },
    );
  }

  getNameMap(ids: string[]): Observable<{ [key: string]: string }> {
    const orgIds = ids
      .filter((id) => !this.organizationsNameMap[id])
      .filter(this.unique);
    if (orgIds.length === 0) {
      return of(this.generateOrgMap(ids));
    }
    return this.http
      .get<{ content: Organization[] }>(this.baseUrl + this.apiEndpoint, {
        params: { unpaged: true, filters: this.buildOrgIdFilter(orgIds) },
      })
      .pipe(
        tap((page) => {
          page.content.forEach((org) => {
            this.organizationsNameMap[org.id] = org.name;
          });
        }),
        map(() => this.generateOrgMap(ids)),
      );
  }

  private buildOrgIdFilter(ids: string[]): string[] {
    return ids.map((id) => `id:${id}-or`);
  }

  private unique(value: string, index: number, self: string[]): boolean {
    return self.indexOf(value) === index;
  }

  private generateOrgMap(ids: string[]): { [key: string]: string } {
    const orgMap: { [key: string]: string } = {};
    ids.forEach((id) => {
      orgMap[id] = this.organizationsNameMap[id] ?? '??';
    });
    return orgMap;
  }
}
